import { getAutomationTemplateById } from "@repo/app-types/entities/automation";
import type {
  AutomationConfigurationProposal,
  AutomationProposalData,
} from "@repo/app-types/entities/automationProposal";
import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import { appPaths } from "../../app/appPaths.js";
import type { CopilotProposalCardContent } from "../researchAgent/copilotProposalCard.js";
import { buildAutomationProposalEditLocation } from "./automationEditorUrlState.js";

/** What an automation proposal's card in the chat shows and opens. */
export function readAutomationProposalCardContent(
  proposal: CopilotProposalSummary<"automation">,
): CopilotProposalCardContent {
  return {
    resourceType: "automation",
    operation: proposal.kind === "create_automation" ? "create" : "update",
    summary: proposal.data.summary,
    highlights: buildAutomationProposalHighlights(proposal.data),
    review: {
      kind: "open_editor",
      location: buildAutomationProposalEditLocation({
        playbookId: proposal.playbook_id,
        automationId:
          proposal.kind === "create_automation"
            ? undefined
            : proposal.resource.id,
        proposalId: proposal.id,
      }),
    },
    resourceLocation: {
      pathname: appPaths.playbookAutomation.build({
        playbookId: proposal.playbook_id,
        automationId: proposal.resource.id,
      }),
    },
  };
}

/** The card's highlights: a new automation's template, or the fields an update changes, in form order. */
function buildAutomationProposalHighlights(
  data: AutomationProposalData,
): readonly string[] {
  switch (data.kind) {
    case "create_automation":
      return [getAutomationTemplateById(data.template_id).name];
    case "update_automation":
      return CONFIGURATION_HIGHLIGHTS.filter(([, fields]) =>
        fields.some((field) => field in data.configuration),
      ).map(([label]) => label);
  }
}

type ConfigurationField = Exclude<
  keyof (Extract<
    AutomationConfigurationProposal,
    { template_family: "report" }
  > &
    Extract<AutomationConfigurationProposal, { template_family: "coaching" }>),
  "template_family"
>;

/** Each highlight and the fields it stands for, in form order. */
const CONFIGURATION_HIGHLIGHTS: readonly (readonly [
  label: string,
  fields: readonly ConfigurationField[],
])[] = [
  ["Name", ["name"]],
  ["Instructions", ["instructions"]],
  ["Playbooks", ["playbook_ids"]],
  ["Playbook", ["playbook_id"]],
  ["Schedule", ["trigger"]],
  ["Result type", ["result_mode"]],
  ["Recipients", ["recipients"]],
  ["People", ["people"]],
  ["Subscribers", ["subscribers"]],
  ["Delivery", ["delivery"]],
  [
    "Notifications",
    ["should_notify_teammates", "should_notify_managers", "email_recipients"],
  ],
];
